/**
 * O TEMA — e quem o escolhe.
 *
 * ## O defeito que isto conserta (2026-09-07)
 *
 * O tema escuro existia desde que a palheta nasceu, e **ninguém conseguia escolhê-lo**: o
 * `vidro.css` só o oferecia dentro de `@media (prefers-color-scheme: dark)`, isto é, ele
 * acontecia se — e só se — o **sistema operacional** da pessoa já estivesse em escuro. Não havia
 * controle no painel, no inbox nem em lugar nenhum do repositório (varrido: zero ocorrências de
 * `data-theme`, de preferência salva e de botão).
 *
 * A consequência que ninguém viu por semanas: toda a medição de contraste "nos dois temas" era
 * feita **emulando `prefers-color-scheme` no navegador** — uma alavanca que o produto não oferece
 * a ninguém. Media-se trinta vezes um estado que o cliente não alcança.
 *
 * ## Como passou a funcionar, e por que assim
 *
 * **Quem resolve o tema é este arquivo, e o CSS obedece a UM atributo.** O `<html>` sempre carrega
 * `data-theme="claro"` ou `"escuro"` — já resolvido —, e o `vidro.css` tem uma regra só por token.
 *
 * ⚠️ **A alternativa era manter o `@media` e ACRESCENTAR o atributo**, e ela foi recusada por
 * medida: o bloco escuro do `vidro.css` tem **113 linhas** de tokens e comentário com as medições
 * de contraste. Ele existiria duas vezes, e a segunda cópia divergiria da primeira no primeiro dia
 * em que alguém ajustasse um tom — que é exatamente o defeito que este repositório mais registra.
 * **Uma verdade sobre a cor, num lugar só.**
 *
 * **O preço, declarado:** sem JavaScript não há escuro. Para o painel e o inbox isso é indiferente
 * — eles são SPA, e sem JS não há tela nenhuma para colorir. Para não piscar branco antes do React
 * montar, o `index.html` de cada build chama `aplicarTemaSalvo()` **no `<head>`**, antes da
 * primeira pintura.
 *
 * ## Três valores, e o terceiro não é enfeite
 *
 * `sistema` é o padrão, e precisa existir: quem hoje tem o SO em escuro **já vê escuro**, e nascer
 * em `claro` seria tirar dessa pessoa o que ela já tem. Em `sistema`, a troca no SO é seguida ao
 * vivo (`matchMedia`), sem recarregar.
 *
 * ## Onde a escolha mora
 *
 * `localStorage`, como a barra recolhida — **preferência de aparelho, não de conta**. A mesma
 * pessoa quer escuro no notebook à noite e claro no monitor de dia; guardá-la no servidor
 * imporia um dos dois aos dois. E ela é lida dentro de `try` porque em janela anônima o acesso
 * **lança**, e um tema não pode derrubar a aplicação.
 */

export type Tema = "claro" | "escuro" | "sistema";

/** O que de fato é pintado — `sistema` já resolvido. */
export type TemaResolvido = "claro" | "escuro";

const CHAVE = "hugi:tema";

/** O padrão. Ver "Três valores" no cabeçalho: nascer em `claro` regrediria quem já vê escuro. */
export const TEMA_PADRAO: Tema = "sistema";

function ehTema(v: unknown): v is Tema {
  return v === "claro" || v === "escuro" || v === "sistema";
}

/** A preferência guardada. Valor estranho (ou storage indisponível) cai no padrão, sem lançar. */
export function lerTema(): Tema {
  try {
    const v = window.localStorage.getItem(CHAVE);
    return ehTema(v) ? v : TEMA_PADRAO;
  } catch {
    return TEMA_PADRAO;
  }
}

function prefereEscuroNoSistema(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

/** `sistema` vira o que o SO diz agora; os outros dois são eles mesmos. */
export function resolver(tema: Tema): TemaResolvido {
  if (tema === "sistema") return prefereEscuroNoSistema() ? "escuro" : "claro";
  return tema;
}

/**
 * Pinta o tema resolvido no `<html>`.
 *
 * **`color-scheme` vai junto, e não é detalhe:** sem ele o navegador continua desenhando barra de
 * rolagem, `<select>` e seletor de data em claro — ilhas brancas no meio da tela escura.
 */
export function aplicarTema(tema: Tema): TemaResolvido {
  const resolvido = resolver(tema);
  const raiz = document.documentElement;
  // `escuro`/`claro` e não `dark`/`light`: o `vidro.css` inteiro é comentado em português, e duas
  // línguas para a mesma coisa é como o `coexistencia` × `coex` da `0004` nasceu. O `color-scheme`
  // abaixo é da especificação do CSS e continua no vocabulário DELA — não é escolha nossa.
  raiz.setAttribute("data-theme", resolvido);
  raiz.style.colorScheme = resolvido === "escuro" ? "dark" : "light";
  return resolvido;
}

/** Guarda e aplica. Devolve o que ficou pintado. */
export function salvarTema(tema: Tema): TemaResolvido {
  try {
    window.localStorage.setItem(CHAVE, tema);
  } catch {
    // Janela anônima: a escolha não sobrevive ao fechar, e ainda assim vale para esta sessão.
  }
  return aplicarTema(tema);
}

/** Lê a preferência e pinta. É o que o `<head>` de cada build chama antes da primeira pintura. */
export function aplicarTemaSalvo(): TemaResolvido {
  return aplicarTema(lerTema());
}

/**
 * Acompanha a troca no SO **enquanto a preferência for `sistema`**.
 *
 * Sem isto, quem está em `sistema` e muda o SO ao anoitecer ficaria no tema antigo até recarregar —
 * e era justamente isso que o `@media` fazia de graça. Devolve a função que desliga o ouvinte.
 */
export function seguirOSistema(aoTrocar: (resolvido: TemaResolvido) => void): () => void {
  let mq: MediaQueryList;
  try {
    mq = window.matchMedia("(prefers-color-scheme: dark)");
  } catch {
    return () => {};
  }
  const ouvir = () => {
    if (lerTema() !== "sistema") return;
    aoTrocar(aplicarTema("sistema"));
  };
  mq.addEventListener("change", ouvir);
  return () => mq.removeEventListener("change", ouvir);
}
